import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Clock, CheckCircle, XCircle, MapPin } from 'lucide-react';

const AboutUsWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;
  background-color: #f8f9fa;
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
`;

const AboutUsHeader = styled.h1`
  font-size: 42px;
  text-align: center;
  color: #2c3e50;
  margin-bottom: 30px;
  font-weight: 700;
`;

const AboutUsImageContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 30px;
`;

const AboutUsImage = styled.img`
  width: 100%;
  max-width: 800px;
  height: auto;
  display: block;
  border-radius: 12px;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease-in-out;

  &:hover {
    transform: scale(1.05);
  }
`;

const DescriptionContainer = styled.div`
  background-color: #ffffff;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  padding: 30px;
  margin-bottom: 40px;
`;

const DescriptionTitle = styled.h2`
  font-size: 24px;
  color: #2c3e50;
  margin-bottom: 20px;
  font-weight: 600;
`;

const DescriptionText = styled.p`
  font-size: 16px;
  color: #34495e;
  line-height: 1.8;
  margin-bottom: 20px;
`;

const FeaturesList = styled.ul`
  list-style-type: none;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
`;

const FeatureItem = styled.li`
  display: flex;
  align-items: center;
  font-size: 14px;
  color: #2c3e50;

  &:before {
    content: '✓';
    color: #27ae60;
    margin-right: 8px;
    font-weight: bold;
  }
`;

const StatusBadge = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 8px 12px;
  border-radius: 20px;
  font-weight: 600;
  font-size: 14px;
  margin-bottom: 20px;
  background-color: ${props => props.isOpen ? '#e8f5e9' : '#ffebee'};
  color: ${props => props.isOpen ? '#388e3c' : '#d32f2f'};

  svg {
    margin-right: 8px;
  }
`;

const HoursContainer = styled.div`
  background-color: #ffffff;
  border-radius: 12px;
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.1);
  padding: 40px;
  margin-top: 60px;
`;

const HoursHeader = styled.h2`
  font-size: 36px;
  font-weight: 800;
  color: #2c3e50;
  text-align: center;
  margin-bottom: 30px;
  text-transform: uppercase;
  letter-spacing: 3px;
  position: relative;
  
  &:after {
    content: '';
    display: block;
    width: 60px;
    height: 4px;
    background-color: #3498db;
    margin: 15px auto 0;
  }
`;

const HoursList = styled.ul`
  list-style-type: none;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
`;

const HoursItem = styled.li`
  padding: 20px;
  background-color: ${props => props.isOpen ? '#e8f5e9' : '#f8f9fa'};
  border-radius: 10px;
  transition: all 0.3s ease;
  border: 1px solid #e0e0e0;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
  }
`;

const DayName = styled.span`
  font-weight: 700;
  color: #34495e;
  display: block;
  margin-bottom: 10px;
  font-size: 18px;
`;

const HoursText = styled.span`
  font-size: 16px;
  color: ${props => props.isOpen ? '#388e3c' : '#7f8c8d'};
  display: flex;
  align-items: center;
  
  svg {
    margin-right: 8px;
  }
`;

const OpenNowTag = styled.span`
  background-color: #4caf50;
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  margin-left: 10px;
`;

const MapAndAddressContainer = styled.div`
  background-color: #ffffff;
  border-radius: 12px;
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.1);
  padding: 30px;
  margin-top: 40px;
`;

const MapHeader = styled.h2`
  font-size: 28px;
  font-weight: 700;
  color: #2c3e50;
  text-align: center;
  margin-bottom: 20px;
  text-transform: uppercase;
  letter-spacing: 2px;
  
  &:after {
    content: '';
    display: block;
    width: 50px;
    height: 3px;
    background-color: #3498db;
    margin: 10px auto 0;
  }
`;

const AddressText = styled.p`
  font-size: 16px;
  color: #34495e;
  line-height: 1.5;
  margin-bottom: 15px;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    margin-right: 8px;
  }
`;

const MapIframeContainer = styled.div`
  position: relative;
  width: 100%;
  padding-bottom: 50%; // Reduced from 50% to 25% for a shorter map
  height: 0;
  overflow: hidden;
  border-radius: 8px;
`;

const MapIframe = styled.iframe`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: 0;
`;


const AboutUsPage = () => {
  const defaultFeatures = [
    "High-quality halal meats",
    "Fresh groceries",
    "Pakistani snacks and beverages",
    "Authentic spices and ingredients",
    "Cookware for Pakistani cuisine"
  ];
  const [aboutUsData, setAboutUsData] = useState({
    title: "Hoppers Quality Meats & Groceries",
    description: "Hoppers Quality Meats, Grocery Supplies, Halal Meats & Groceries is a Pakistani-owned and managed business located in the western suburbs of Melbourne, Australia. We specialize in providing a wide range of high-quality halal meats and groceries, catering to the needs of the local Pakistani community and those seeking authentic Pakistani cuisine.",
    features: defaultFeatures,
    image: "/default-image.jpg",
    hours: {
      monday: { opening: "09:00", closing: "18:00" },
      tuesday: { opening: "09:00", closing: "18:00" },
      wednesday: { opening: "09:00", closing: "18:00" },
      thursday: { opening: "09:00", closing: "18:00" },
      friday: { opening: "09:00", closing: "18:30" },
      saturday: { opening: "09:00", closing: "18:00" },
      sunday: { opening: "10:00", closing: "17:00" },
    },
    address: "8/70 Warringa Cres, Hoppers Crossing VIC 3029",
    mapUrl: "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d6298.031943682553!2d144.68686701180872!3d-37.883309171841354!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x6ad68f56e0f0df65%3A0x71ed739a1f13b1db!2sHoppers%20Quality%20Meat%20%26%20Grocery!5e0!3m2!1sen!2sau!4v1726873007345!5m2!1sen!2sau"
  });


  const [currentDay, setCurrentDay] = useState('');
  const [currentTime, setCurrentTime] = useState('');
  const [isCurrentlyOpen, setIsCurrentlyOpen] = useState(false);

  useEffect(() => {
    const fetchAboutUsData = async () => {
      try {
        const response = await fetch('/api/about-us/');
        const data = await response.json();
        setAboutUsData(prevData => ({
          ...prevData,
          title: data.title || prevData.title,
          description: data.description || prevData.description,
          image: data.image || prevData.image,
          features: data.features && data.features.length > 0 
          ? data.features.map(f => f.name) 
          : defaultFeatures,
          hours: {
            monday: {
              opening: data.opening_hours?.mon?.opening || prevData.hours.monday.opening,
              closing: data.opening_hours?.mon?.closing || prevData.hours.monday.closing
            },
            tuesday: {
              opening: data.opening_hours?.tue?.opening || prevData.hours.tuesday.opening,
              closing: data.opening_hours?.tue?.closing || prevData.hours.tuesday.closing
            },
            wednesday: {
              opening: data.opening_hours?.wed?.opening || prevData.hours.wednesday.opening,
              closing: data.opening_hours?.wed?.closing || prevData.hours.wednesday.closing
            },
            thursday: {
              opening: data.opening_hours?.thu?.opening || prevData.hours.thursday.opening,
              closing: data.opening_hours?.thu?.closing || prevData.hours.thursday.closing
            },
            friday: {
              opening: data.opening_hours?.fri?.opening || prevData.hours.friday.opening,
              closing: data.opening_hours?.fri?.closing || prevData.hours.friday.closing
            },
            saturday: {
              opening: data.opening_hours?.sat?.opening || prevData.hours.saturday.opening,
              closing: data.opening_hours?.sat?.closing || prevData.hours.saturday.closing
            },
            sunday: {
              opening: data.opening_hours?.sun?.opening || prevData.hours.sunday.opening,
              closing: data.opening_hours?.sun?.closing || prevData.hours.sunday.closing
            }
          }
        }));
      } catch (error) {
        console.error("Error fetching About Us data:", error);
      }
    };

    fetchAboutUsData();

    const updateCurrentTime = () => {
      const now = new Date();
      setCurrentDay(now.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase());
      setCurrentTime(now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false }));
    };

    updateCurrentTime();
    const intervalId = setInterval(updateCurrentTime, 60000); // Update every minute

    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    const checkIfOpen = () => {
      if (currentDay && aboutUsData.hours[currentDay]) {
        const todayHours = aboutUsData.hours[currentDay];
        if (todayHours.opening !== "Closed" && todayHours.closing !== "Closed") {
          setIsCurrentlyOpen(currentTime >= todayHours.opening && currentTime < todayHours.closing);
        } else {
          setIsCurrentlyOpen(false);
        }
      } else {
        setIsCurrentlyOpen(false);
      }
    };

    checkIfOpen();
  }, [currentDay, currentTime, aboutUsData.hours]);

  const formatTime = (time) => {
    if (time === "Closed") return time;
    const [hours, minutes] = time.split(':');
    return new Date(2023, 0, 1, hours, minutes).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  return (
    <AboutUsWrapper>
      <AboutUsHeader>{aboutUsData.title}</AboutUsHeader>

      <AboutUsImageContainer>
        <AboutUsImage src={aboutUsData.image} alt="About Us" />
      </AboutUsImageContainer>

      <DescriptionContainer>
        <StatusBadge isOpen={isCurrentlyOpen}>
          {isCurrentlyOpen ? (
            <>
              <CheckCircle size={18} />
              Open Now
            </>
          ) : (
            <>
              <XCircle size={18} />
              Closed Now
            </>
          )}
        </StatusBadge>
        <DescriptionTitle>About Us</DescriptionTitle>
        <DescriptionText>{aboutUsData.description}</DescriptionText>
        <FeaturesList>
          {aboutUsData.features.map((feature, index) => (
            <FeatureItem key={index}>{feature}</FeatureItem>
          ))}
        </FeaturesList>
      </DescriptionContainer>

      <HoursContainer>
        <HoursHeader>Working Hours</HoursHeader>
        <HoursList>
          {Object.entries(aboutUsData.hours).map(([day, hours]) => {
            const isOpen = hours.opening !== "Closed" && hours.closing !== "Closed";
            const isOpenNow = day === currentDay && isCurrentlyOpen;
            return (
              <HoursItem key={day} isOpen={isOpenNow}>
                <DayName>{day.charAt(0).toUpperCase() + day.slice(1)}</DayName>
                <HoursText isOpen={isOpen}>
                  <Clock size={18} />
                  {isOpen ? `${formatTime(hours.opening)} - ${formatTime(hours.closing)}` : "Closed"}
                  {isOpenNow && <OpenNowTag>Open Now</OpenNowTag>}
                </HoursText>
              </HoursItem>
            );
          })}
        </HoursList>
      </HoursContainer>
      <MapAndAddressContainer>
        <MapHeader>Our Location</MapHeader>
        <AddressText>
          <MapPin size={25} />
          {aboutUsData.address}
        </AddressText>
        <MapIframeContainer>
          <MapIframe 
            src={aboutUsData.mapUrl} 
            allowFullScreen="" 
            loading="lazy" 
            referrerPolicy="no-referrer-when-downgrade"
          />
        </MapIframeContainer>
      </MapAndAddressContainer>
    </AboutUsWrapper>
  );
};

export default AboutUsPage;